import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi } from "@/lib/utils";
import { invalidatePostedTuitionLedgerQueries } from "@/features/tuition/hooks/invalidate-posted-tuition-ledger-queries";
import {
  ClassEnrollmentsApi,
  type CreateBulkEnrollmentResponse,
} from "@/openapi/generated";

export type ClassEnrollmentCreateVariables = {
  studentId: number;
  classIds: number[];
  suppressToast?: boolean;
};

export const useClassEnrollmentCreate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, mutateAsync, isPending, isSuccess, isError } = useMutation<
    CreateBulkEnrollmentResponse,
    Error,
    ClassEnrollmentCreateVariables
  >({
    mutationFn: async ({ studentId, classIds }) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      if (classIds.length === 0) {
        throw new Error("At least one class is required");
      }
      const service = createAuthenticatedApi(ClassEnrollmentsApi, accessToken);
      return service.apiClassEnrollmentsBulkPost({
        createBulkEnrollmentRequest: {
          studentId,
          classIds,
        },
      });
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.ClassesEnrollmentsByStudentId],
      });
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Students],
      });
      await queryClient.invalidateQueries({
        predicate: (query) => {
          return (
            Array.isArray(query.queryKey) &&
            query.queryKey.some(
              (key) =>
                typeof key === "string" &&
                (key.includes("enrollment") || key.includes("Enrollment")),
            )
          );
        },
      });
      await invalidatePostedTuitionLedgerQueries(queryClient);

      if (variables.suppressToast) {
        return;
      }

      const created = data.created ?? [];
      const failed = data.failed ?? [];

      if (created.length > 0 && failed.length === 0) {
        toast({
          title: "Success",
          description: "Student successfully enrolled in class!",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        });
      } else if (failed.length > 0) {
        const errorObj = failed[0]?.error;
        const errorMessage =
          typeof errorObj === "string"
            ? errorObj
            : errorObj?.message || "Failed to enroll student";
        toast({
          title: "Enrollment Failed",
          description: errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      }
    },
    onError: (error, variables) => {
      if (variables.suppressToast) {
        return;
      }

      const errorMessage =
        error instanceof Error
          ? error.message
          : "Failed to enroll student in class";

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, mutateAsync, isPending, isError, isSuccess };
};
